import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as api from './api';

// TDD_107a — Coach Analytics web dashboard. One view: roster + per-athlete
// drill-down, consuming the five TDD_107 Phase 1 endpoints. Caller identity is
// the JWT, server-side, exclusively — this component never receives or sends a
// caller profile id; every id below is a TARGET athlete taken from roster data.

// local_date must be built from LOCAL calendar components. toISOString() is
// UTC and reads the wrong date for several hours a day in any non-UTC
// timezone — the server derives the athlete's week window from this value.
export function formatLocalDate(d) {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

const FULL_FETCH_PAGE = 200;      // server max — fewest round-trips to completion
const FULL_FETCH_MAX_PAGES = 50;  // 10k athletes; rosters are "a team, not a feed"

// Wire strings are title-case (the mobile client's lowercase compare is a
// known, separate bug). Keyed exactly as the API emits them.
const RECOVERY_PILLS = {
  'High Risk':        { cls: 'cv-pill-hi', label: 'HIGH RISK' },
  'Partial Recovery': { cls: 'cv-pill-pr', label: 'PARTIAL' },
  'Trainable':        { cls: 'cv-pill-tr', label: 'TRAINABLE' },
  'Fully Recovered':  { cls: 'cv-pill-fr', label: 'RECOVERED' },
  'Not Trained':      { cls: 'cv-pill-nt', label: 'NOT TRAINED' },
};

const AVATAR_COLORS = ['#C3F53B', '#c3d2e8', '#22c55e', '#e5c04b', '#f87171', '#FF5CA8'];

// Client-side sort config. The server is only ever asked for name/asc (stable
// full-fetch paging); these accessors sort the complete held set locally.
const SORT_COLUMNS = [
  { key: 'name', label: 'Athlete', val: r => `${r.first_name || ''} ${r.last_name || ''}`.trim().toLowerCase() },
  { key: 'workouts_this_week', label: 'Workouts', val: r => r.workouts_this_week },
  { key: 'adherence_pct', label: 'Adherence', val: r => r.adherence_pct },
  { key: 'volume_trend', label: 'Volume', val: r => r.volume_trend_pct },
  { key: 'nutrition_pct', label: 'Nutrition', val: r => r.nutrition_pct },
  { key: 'last_active', label: 'Last active', val: r => r.last_active },
];

function initials(r) {
  return `${(r.first_name || '?')[0] || ''}${(r.last_name || '')[0] || ''}`.toUpperCase();
}

function lastActiveText(r) {
  const d = r.days_since_active;
  if (d === null || d === undefined) return 'never';
  if (d === 0) return 'today';
  if (d === 1) return 'yesterday';
  return `${d}d ago`;
}

function relText(r) {
  const parts = [];
  if ((r.relationship_types || []).includes('direct_coach')) parts.push('Direct');
  if ((r.relationship_types || []).includes('org')) parts.push('Org');
  return parts.join(' · ') || '—';
}

function errorText(e) {
  if (e && e.status) return `Request failed (${e.status})`;
  return 'Network error';
}

export default function CoachView({ onUnauthorized, onAdjustPlan }) {
  const [rosterState, setRosterState] = useState({ status: 'loading' });
  const [filter, setFilter] = useState('all');           // 'all' | 'org' | 'direct'
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [revealId, setRevealId] = useState(null);        // athlete id with the >100 adherence reveal open
  const [notice, setNotice] = useState(null);            // consent-revoked banner over the roster

  const [selected, setSelected] = useState(null);        // roster item being drilled into
  const [recovery, setRecovery] = useState(null);        // {status, data?, message?}
  const [workouts, setWorkouts] = useState(null);        // {status, items?, message?} — completed only
  const [selectedWorkoutId, setSelectedWorkoutId] = useState(null);
  const [sets, setSets] = useState(null);                // {status, data?, message?} — status 'gone' = stale 404
  const [nutrition, setNutrition] = useState(null);

  // One generation counter guards every async paint: a response is applied only
  // if nothing (athlete switch, roster refetch, unmount) superseded it. A slow
  // response for athlete A must never paint over athlete B.
  const genRef = useRef(0);

  useEffect(() => {
    // Velocity uses Khand/Saira/Chakra Petch; the view owns its styling, so it
    // loads its own fonts (idempotent — survives tab switches).
    if (typeof document !== 'undefined' && !document.getElementById('cv-fonts')) {
      const link = document.createElement('link');
      link.id = 'cv-fonts';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Khand:wght@400;500;600;700&family=Saira:wght@400;500;600&family=Chakra+Petch:wght@500;700&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  function is401(e) {
    if (e && e.status === 401) {
      onUnauthorized();
      return true;
    }
    return false;
  }

  async function fetchRoster() {
    const gen = ++genRef.current;
    setRosterState({ status: 'loading' });
    // Computed once per roster fetch, from local calendar components.
    const localDate = formatLocalDate(new Date());
    try {
      let items = [];
      let total = 0;
      let offset = 0;
      for (let page = 0; page < FULL_FETCH_MAX_PAGES; page++) {
        // Fixed sort=name/asc for stable paging; all user-facing sorting is
        // client-side over the complete set. Never free text.
        const res = await api.getCoachRoster({ localDate, sort: 'name', direction: 'asc', limit: FULL_FETCH_PAGE, offset });
        if (gen !== genRef.current) return;
        total = res.total;
        items = items.concat(res.items || []);
        if (!res.has_more || !(res.items || []).length) break;
        offset += res.items.length;
      }
      const seen = new Set();
      const deduped = items.filter(r => !seen.has(r.athlete_fp_id) && seen.add(r.athlete_fp_id));
      setRosterState({ status: 'ready', items: deduped, total });
    } catch (e) {
      if (gen !== genRef.current) return;
      if (is401(e)) return;
      // Partial failure mid-loop = whole-roster error, never a silently
      // truncated roster.
      setRosterState({ status: 'error', message: errorText(e) });
    }
  }

  useEffect(() => { fetchRoster(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 403 mid-session: consent was revoked. Bounce to the roster and refetch it —
  // the athlete must also disappear from the list.
  function consentRevoked() {
    setSelected(null);
    setNotice('This athlete is no longer sharing data with you.');
    fetchRoster(); // bumps the generation, so in-flight drill-down responses discard
  }

  async function fetchRecovery(athleteId, gen) {
    setRecovery({ status: 'loading' });
    try {
      const res = await api.getStudentMuscleRecovery(athleteId);
      if (gen !== genRef.current) return;
      setRecovery({ status: 'ready', data: res });
    } catch (e) {
      if (gen !== genRef.current) return;
      if (is401(e)) return;
      if (e && e.status === 403) return consentRevoked();
      setRecovery({ status: 'error', message: errorText(e) });
    }
  }

  async function fetchNutrition(athleteId, gen) {
    setNutrition({ status: 'loading' });
    try {
      const res = await api.getStudentNutritionSummary(athleteId);
      if (gen !== genRef.current) return;
      setNutrition({ status: 'ready', data: res });
    } catch (e) {
      if (gen !== genRef.current) return;
      if (is401(e)) return;
      if (e && e.status === 403) return consentRevoked();
      setNutrition({ status: 'error', message: errorText(e) });
    }
  }

  async function fetchSets(athleteId, workoutId, gen) {
    setSets({ status: 'loading' });
    try {
      const res = await api.getStudentWorkoutSets(athleteId, workoutId);
      if (gen !== genRef.current) return;
      setSets({ status: 'ready', data: res });
    } catch (e) {
      if (gen !== genRef.current) return;
      if (is401(e)) return;
      if (e && e.status === 403) return consentRevoked();
      if (e && e.status === 404) {
        // Stale picker selection — deleted, or aged past the 90-day window.
        // Keep the drill-down; only this card reports it.
        setSets({ status: 'gone' });
        return;
      }
      setSets({ status: 'error', message: errorText(e) });
    }
  }

  async function fetchWorkoutsList(athleteId, gen) {
    setWorkouts({ status: 'loading' });
    setSets(null);
    setSelectedWorkoutId(null);
    try {
      const res = await api.getStudentWorkouts(athleteId);
      if (gen !== genRef.current) return;
      // Title-case wire strings — 'Completed', never 'completed'.
      const completed = (res || []).filter(w => w.status === 'Completed');
      setWorkouts({ status: 'ready', items: completed });
      if (completed.length) {
        setSelectedWorkoutId(completed[0].id);
        fetchSets(athleteId, completed[0].id, gen);
      }
    } catch (e) {
      if (gen !== genRef.current) return;
      if (is401(e)) return;
      if (e && e.status === 403) return consentRevoked();
      setWorkouts({ status: 'error', message: errorText(e) });
    }
  }

  function selectAthlete(r) {
    const gen = ++genRef.current;
    setSelected(r);
    setRevealId(null);
    fetchRecovery(r.athlete_fp_id, gen);
    fetchWorkoutsList(r.athlete_fp_id, gen);
    fetchNutrition(r.athlete_fp_id, gen);
  }

  function pickWorkout(workoutId) {
    if (!selected || workoutId === selectedWorkoutId) return;
    setSelectedWorkoutId(workoutId);
    fetchSets(selected.athlete_fp_id, workoutId, genRef.current);
  }

  // ── Derived roster ─────────────────────────────────────────────────────────
  // Own useMemo so the empty-array fallback is referentially stable —
  // otherwise every render mints a new [] and invalidates the memo below
  // (react-hooks/exhaustive-deps, enforced as an error by the CI build).
  const items = useMemo(
    () => (rosterState.status === 'ready' ? rosterState.items : []),
    [rosterState]
  );

  // Noun rule over the COMPLETE fetched set: pure personal trainer (every
  // athlete's relationship is exactly ['direct_coach']) says "clients".
  const isPT = items.length > 0 && items.every(r =>
    (r.relationship_types || []).length === 1 && r.relationship_types[0] === 'direct_coach');
  const noun = isPT ? 'client' : 'athlete';

  const visible = useMemo(() => {
    let list = items;
    if (filter === 'org') list = list.filter(r => (r.relationship_types || []).includes('org'));
    if (filter === 'direct') list = list.filter(r => (r.relationship_types || []).includes('direct_coach'));
    if (attentionOnly) list = list.filter(r => r.needs_attention);
    const col = SORT_COLUMNS.find(c => c.key === sortKey) || SORT_COLUMNS[0];
    const ranked = list.filter(r => col.val(r) !== null && col.val(r) !== undefined);
    const unranked = list.filter(r => col.val(r) === null || col.val(r) === undefined);
    const cmp = (a, b) => {
      const va = col.val(a);
      const vb = col.val(b);
      let c = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
      if (sortDir === 'desc') c = -c;
      // Ties break ascending on athlete_fp_id — mirrors the server's
      // stable-ordering rule.
      return c || (a.athlete_fp_id - b.athlete_fp_id);
    };
    ranked.sort(cmp);
    // Nulls always sort last regardless of direction.
    unranked.sort((a, b) => a.athlete_fp_id - b.athlete_fp_id);
    return ranked.concat(unranked);
  }, [items, filter, attentionOnly, sortKey, sortDir]);

  const flaggedCount = items.filter(r => r.needs_attention).length;

  function setSort(key) {
    setRevealId(null); // sort/filter closes any open tap-reveal
    if (key === sortKey) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  }

  function setFilterChip(f) {
    setRevealId(null);
    setFilter(f);
  }

  function toggleAttention() {
    setRevealId(null);
    setAttentionOnly(v => !v);
  }

  // ── Cell renderers ─────────────────────────────────────────────────────────
  function adherenceCell(r) {
    const a = r.adherence_pct;
    if (a === null || a === undefined) return <span className="cv-vmuted">— no prog</span>;
    if (a === 0) return <span className="cv-vbad">0%</span>;
    if (a > 100) {
      return (
        <button
          className="cv-over"
          onClick={e => { e.stopPropagation(); setRevealId(revealId === r.athlete_fp_id ? null : r.athlete_fp_id); }}
        >100+</button>
      );
    }
    if (a === 100) return <span className="cv-vgood">✓ on plan</span>;
    return <span>{Math.round(a)}%</span>;
  }

  function volumeCell(r) {
    const v = r.volume_trend_pct;
    if (v === null || v === undefined) return <span className="cv-vmuted">—</span>;
    return v >= 0
      ? <span className="cv-vgood">▲{Math.round(v)}%</span>
      : <span className="cv-vbad">▼{Math.abs(Math.round(v))}%</span>;
  }

  function nutritionCell(r) {
    if (r.nutrition_no_goal_set) return <span className="cv-vmuted">— no goal</span>;
    if (r.nutrition_pct === null || r.nutrition_pct === undefined) return <span className="cv-vmuted">—</span>;
    return <span>{Math.round(r.nutrition_pct)}%</span>;
  }

  const trustFooter = (
    <div className="cv-trust">
      Health metrics are not shown to coaches. Steps, sleep, resting heart rate &amp; active calories stay private.
    </div>
  );

  // ── Roster view ────────────────────────────────────────────────────────────
  function renderRoster() {
    if (rosterState.status === 'loading') {
      return (
        <div className="cv-shell">
          <div className="cv-skeleton" data-testid="roster-loading">Loading roster…</div>
        </div>
      );
    }
    if (rosterState.status === 'error') {
      return (
        <div className="cv-shell">
          <div className="cv-error">
            <div>Couldn't load your roster. {rosterState.message}</div>
            <button className="cv-retry" onClick={fetchRoster}>Retry</button>
          </div>
        </div>
      );
    }
    if (!items.length) {
      return (
        <div className="cv-shell">
          {notice && <div className="cv-notice">{notice}</div>}
          <div className="cv-empty">
            <div className="cv-empty-hd">No athletes yet.</div>
            <div>
              Athletes appear here once they've accepted coaching <b>and agreed to share their
              training data</b>. If someone's missing, ask them to check their sharing settings in the app.
            </div>
            <div className="cv-empty-how">
              <b>New to coaching?</b> There's no application — coaching starts by invitation. An athlete
              invites you as their coach from their FitMagic app, or an organization adds you as a coach,
              owner, or admin. Accept the invite and your athletes appear here once they share their data.
            </div>
          </div>
          {trustFooter}
        </div>
      );
    }

    return (
      <div className="cv-shell">
        {notice && <div className="cv-notice">{notice}</div>}
        <div className="cv-rhead">
          <h2 className="cv-h2">Roster</h2>
          <span className="cv-rsub">{items.length} {noun}{items.length === 1 ? '' : 's'} · this week</span>
        </div>

        <button
          className={`cv-attnbar ${flaggedCount ? 'cv-attnbar-warn' : ''} ${attentionOnly ? 'cv-attnbar-on' : ''}`}
          onClick={toggleAttention}
        >
          {flaggedCount
            ? `⚠ ${flaggedCount} ${noun}${flaggedCount === 1 ? '' : 's'} inactive 7+ days`
            : 'Everyone on track ✓'}
          <span className="cv-attnst">{attentionOnly ? 'showing flagged — clear' : 'tap to filter'}</span>
        </button>

        <div className="cv-chips">
          {[['all', 'All'], ['org', 'Org'], ['direct', 'Direct']].map(([f, label]) => (
            <button key={f} className={`cv-chip ${filter === f ? 'cv-chip-on' : ''}`} onClick={() => setFilterChip(f)}>
              {label}
            </button>
          ))}
        </div>

        <div className="cv-tablewrap">
          <div className="cv-table" role="table" aria-label="Roster">
            <div className="cv-tr cv-th" role="row">
              {SORT_COLUMNS.map(c => (
                <button key={c.key} role="columnheader" className={`cv-thc ${sortKey === c.key ? 'cv-thc-on' : ''}`}
                        onClick={() => setSort(c.key)}>
                  {c.label}{sortKey === c.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </button>
              ))}
            </div>
            {visible.map(r => (
              <React.Fragment key={r.athlete_fp_id}>
                <div className={`cv-tr cv-row ${r.needs_attention ? 'cv-row-attn' : ''}`} role="row"
                     onClick={() => selectAthlete(r)}>
                  <div className="cv-td cv-td-ath" data-label="">
                    <span className="cv-av" style={{ background: AVATAR_COLORS[r.athlete_fp_id % AVATAR_COLORS.length] }}>
                      {initials(r)}
                    </span>
                    <span className="cv-nm">
                      <span className="cv-nmn">
                        {r.first_name} {r.last_name}
                        {/* Injury placeholder — decorative slot only. No data source
                            exists; no text, no tooltip, no interaction, and it is
                            excluded from the accessibility tree. */}
                        <span className="cv-injslot" aria-hidden="true">◆</span>
                        {r.needs_attention && <span className="cv-flag">⚠ INACTIVE 7+ DAYS</span>}
                      </span>
                      <span className="cv-rel">{relText(r)}</span>
                    </span>
                  </div>
                  <div className="cv-td" data-label="Workouts">
                    {r.workouts_this_week}{r.expected_per_week ? `/${r.expected_per_week}` : ''}
                  </div>
                  <div className="cv-td" data-label="Adherence">{adherenceCell(r)}</div>
                  <div className="cv-td" data-label="Volume">{volumeCell(r)}</div>
                  <div className="cv-td" data-label="Nutrition">{nutritionCell(r)}</div>
                  <div className="cv-td" data-label="Last active">{lastActiveText(r)}</div>
                </div>
                {revealId === r.athlete_fp_id && (
                  <div className="cv-reveal">
                    completed {r.workouts_this_week} of {r.expected_per_week} planned sessions
                  </div>
                )}
              </React.Fragment>
            ))}
            {!visible.length && <div className="cv-nomatch">No {noun}s match this filter.</div>}
          </div>
        </div>
        <div className="cv-footnote">Volume trend: reps × weight this week vs last.</div>
        {trustFooter}
      </div>
    );
  }

  // ── Drill-down cards ───────────────────────────────────────────────────────
  function cardError(state, retry) {
    return (
      <div className="cv-carderr">
        <span>{state.message || 'Something went wrong.'}</span>
        <button className="cv-retry" onClick={retry}>Retry</button>
      </div>
    );
  }

  function renderRecoveryCard() {
    let body;
    if (!recovery || recovery.status === 'loading') {
      body = <div className="cv-skeleton">Loading…</div>;
    } else if (recovery.status === 'error') {
      body = cardError(recovery, () => fetchRecovery(selected.athlete_fp_id, genRef.current));
    } else {
      const muscles = (recovery.data && recovery.data.muscles) || [];
      body = muscles.length ? muscles.map(m => {
        const pill = RECOVERY_PILLS[m.status] || { cls: 'cv-pill-nt', label: m.status };
        return (
          <div className="cv-musc" key={m.muscle}>
            <span className={`cv-pill ${pill.cls}`}>{pill.label}</span>
            <span className="cv-muscname">{m.muscle}</span>
            {/* Score is null outside the 7-day window: pill + days only. */}
            {m.recovery_score !== null && m.recovery_score !== undefined && (
              <span className="cv-score">{Math.round(m.recovery_score)}</span>
            )}
            <span className="cv-dago">{m.days_ago}d ago</span>
          </div>
        );
      }) : <div className="cv-cardempty">No completed workouts yet.</div>;
    }
    return (
      <div className="cv-card">
        <h3 className="cv-h3">Muscle recovery <span className="cv-h3r">score · days since trained</span></h3>
        {body}
      </div>
    );
  }

  function renderWorkoutsCard() {
    let body;
    if (!workouts || workouts.status === 'loading') {
      body = <div className="cv-skeleton">Loading…</div>;
    } else if (workouts.status === 'error') {
      body = cardError(workouts, () => fetchWorkoutsList(selected.athlete_fp_id, genRef.current));
    } else if (!workouts.items.length) {
      body = <div className="cv-cardempty">No completed workouts in the last 90 days.</div>;
    } else {
      const picker = (
        <div className="cv-wsel">
          {workouts.items.map(w => (
            <button key={w.id} className={`cv-w ${w.id === selectedWorkoutId ? 'cv-w-on' : ''}`}
                    onClick={() => pickWorkout(w.id)}>
              <span className="cv-wt">{w.name || 'Workout'}</span>
              <span className="cv-wd">{w.scheduled_workout_date || ''}</span>
            </button>
          ))}
        </div>
      );
      let table = null;
      if (!sets || sets.status === 'loading') {
        table = <div className="cv-skeleton">Loading sets…</div>;
      } else if (sets.status === 'gone') {
        table = <div className="cv-cardempty">This workout is no longer available.</div>;
      } else if (sets.status === 'error') {
        table = cardError(sets, () => fetchSets(selected.athlete_fp_id, selectedWorkoutId, genRef.current));
      } else {
        const payload = sets.data;
        // Capability flags gate columns (forward-compatible with TDD_108) —
        // no greyed "coming soon" column when false.
        const showPlanLoad = !!payload.load_adherence_available;
        const showPR = !!payload.pr_badges_available;
        const groups = [];
        for (const s of payload.sets || []) {
          const name = s.workout_group || 'Main Workout';
          let g = groups.find(x => x.name === name);
          if (!g) { g = { name, sets: [] }; groups.push(g); }
          g.sets.push(s);
        }
        const cols = 6 + (showPlanLoad ? 1 : 0);
        table = (
          <>
            {groups.map(g => (
              <div key={g.name}>
                <div className="cv-grp">{g.name}</div>
                <div className="cv-setscroll">
                  <table className="cv-sets">
                    <thead>
                      <tr>
                        <th className="cv-l">Exercise</th><th>Set</th><th>Plan reps</th><th>Actual reps</th>
                        {showPlanLoad && <th>Plan load</th>}
                        <th>Load lifted</th><th>Eff.</th><th>Rest</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.sets.map(s => {
                        const under = s.actual_reps !== null && s.prescribed_reps !== null && s.actual_reps < s.prescribed_reps;
                        // Band sets carry no weight by data-model rule — the band
                        // color renders where the load would.
                        const load = s.band_color
                          ? <span className="cv-band">{s.band_color}</span>
                          : (s.weight !== null && s.weight !== undefined ? `${s.weight} lb` : '—');
                        return (
                          <React.Fragment key={s.id}>
                            <tr>
                              <td className="cv-l cv-ex">
                                {s.exercise_name || '—'}
                                {s.superset_group && <span className="cv-ss"> {s.superset_group}{s.round_number ? `·R${s.round_number}` : ''}</span>}
                                {showPR && s.is_personal_best && <span className="cv-pr"> PR</span>}
                              </td>
                              <td>{s.set_num}</td>
                              <td className="cv-plan">{s.prescribed_reps ?? '—'}</td>
                              <td className={under ? 'cv-repmiss' : 'cv-rephit'}>{s.actual_reps ?? '—'}</td>
                              {showPlanLoad && <td>{s.prescribed_weight ?? '—'}</td>}
                              <td>{load}</td>
                              <td>{s.effort_pct !== null && s.effort_pct !== undefined ? `${s.effort_pct}%` : '—'}</td>
                              <td>{s.rest_seconds !== null && s.rest_seconds !== undefined ? `${s.rest_seconds}s` : '—'}</td>
                            </tr>
                            {s.notes && (
                              <tr><td colSpan={cols + 1} className="cv-notecell">"{s.notes}"</td></tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            <div className="cv-cap">
              Reps show planned vs performed. Prescribed weight isn't preserved yet — only the load lifted is shown.
            </div>
          </>
        );
      }
      body = <>{picker}{table}</>;
    }
    return (
      <div className="cv-card cv-card-wide">
        <h3 className="cv-h3">Workouts <span className="cv-h3r">last 90 days</span></h3>
        {body}
      </div>
    );
  }

  function renderNutritionCard() {
    let body;
    if (!nutrition || nutrition.status === 'loading') {
      body = <div className="cv-skeleton">Loading…</div>;
    } else if (nutrition.status === 'error') {
      body = cardError(nutrition, () => fetchNutrition(selected.athlete_fp_id, genRef.current));
    } else {
      const d = nutrition.data || {};
      const days = d.days || [];
      const logged = days.filter(x => x.total_calories !== null && x.total_calories !== undefined);
      const avg = field => {
        const vals = logged.map(x => parseFloat(x[field])).filter(v => !Number.isNaN(v));
        return vals.length ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length) : null;
      };
      if (!logged.length) {
        body = <div className="cv-cardempty">No nutrition logged this week.</div>;
      } else if (d.no_goal_set) {
        // No fabricated percentages, ever: absolute averages only.
        body = (
          <>
            <div className="cv-nogoal">⚠ No calorie goal set — logged totals only.</div>
            <div className="cv-bars">
              <Bar label="Calories" text={`${avg('total_calories') ?? '—'} avg`} pct={null} />
              <Bar label="Protein" text={`${avg('total_protein') ?? '—'} g avg`} pct={null} />
            </div>
            <div className="cv-cap">Logged {logged.length} of 7 days.</div>
          </>
        );
      } else {
        const t = d.targets || {};
        const c = d.compliance || {};
        // Calories & protein % come from the API; carb/fat bars are computed
        // client-side from days[] vs targets (contract fact 5).
        const carbsPct = avg('total_carbs') !== null && t.carbs_g ? Math.round(avg('total_carbs') / t.carbs_g * 100) : null;
        const fatPct = avg('total_fat') !== null && t.fat_g ? Math.round(avg('total_fat') / t.fat_g * 100) : null;
        body = (
          <>
            <div className="cv-bars">
              <Bar label="Calories" pct={c.calories_pct} text={`${avg('total_calories') ?? '—'} / ${t.calorie_goal ?? '—'}`} />
              <Bar label="Protein" pct={c.protein_pct} text={`${avg('total_protein') ?? '—'} / ${t.protein_g ?? '—'} g`} />
              <Bar label="Carbs" pct={carbsPct} text={`${avg('total_carbs') ?? '—'} / ${t.carbs_g ?? '—'} g`} />
              <Bar label="Fat" pct={fatPct} text={`${avg('total_fat') ?? '—'} / ${t.fat_g ?? '—'} g`} />
            </div>
            <div className="cv-cap">Logged {c.days_logged ?? logged.length} of 7 days.</div>
          </>
        );
      }
    }
    return (
      <div className="cv-card">
        <h3 className="cv-h3">Nutrition <span className="cv-h3r">7-day vs goal</span></h3>
        {body}
        <div className="cv-cap">Daily totals only. Individual foods are never shown to coaches.</div>
      </div>
    );
  }

  // Phase-2 preview, non-interactive by ruling: greyed, exactly this label, no
  // capability claims. Web-only surface — never packaged into the iOS build.
  const aiPanel = (
    <div className="cv-card cv-card-wide cv-soon" aria-disabled="true">
      <h3 className="cv-h3">AI Assistant — soon</h3>
    </div>
  );

  function renderDrilldown() {
    const r = selected;
    // PT-noun mode leads with the workouts card.
    const halfCards = isPT
      ? [renderNutritionCard(), renderRecoveryCard()]
      : [renderRecoveryCard(), renderNutritionCard()];
    const cardStack = isPT
      ? <>{renderWorkoutsCard()}<div className="cv-cols">{halfCards[0]}{halfCards[1]}</div></>
      : <><div className="cv-cols">{halfCards[0]}{halfCards[1]}</div>{renderWorkoutsCard()}</>;
    return (
      <div className="cv-shell">
        <button className="cv-back" onClick={() => setSelected(null)}>← Roster</button>
        <div className="cv-dh">
          <span className="cv-av cv-av-lg" style={{ background: AVATAR_COLORS[r.athlete_fp_id % AVATAR_COLORS.length] }}>
            {initials(r)}
          </span>
          <span>
            <h2 className="cv-h2">{r.first_name} {r.last_name}</h2>
            <div className="cv-dsub">{relText(r)} · last active {lastActiveText(r)}</div>
          </span>
          <span className="cv-spacer" />
          <button className="cv-adjust" onClick={onAdjustPlan}>✎ Adjust plan</button>
        </div>
        {cardStack}
        {aiPanel}
        {trustFooter}
      </div>
    );
  }

  return (
    <div className="cv-root">
      <style>{CV_CSS}</style>
      {selected ? renderDrilldown() : renderRoster()}
    </div>
  );
}

function Bar({ label, pct, text }) {
  const width = pct === null || pct === undefined ? 60 : Math.max(2, Math.min(100, Math.round(pct)));
  const muted = pct === null || pct === undefined;
  return (
    <div className="cv-bar">
      <span className="cv-bk">{label}</span>
      <span className="cv-track"><span className={`cv-fill ${muted ? 'cv-fill-muted' : ''}`} style={{ width: `${width}%` }} /></span>
      <span className="cv-bv">{text}{!muted && ` · ${Math.round(pct)}%`}</span>
    </div>
  );
}

// Velocity — volt on carbon, per the TDD_107 mockups. Scoped to .cv-root; the
// builder chrome keeps its own palette.
const CV_CSS = `
.cv-root{
  --cv-bg:#0B0C09; --cv-card:#111309; --cv-card2:#171A0F; --cv-border:#2a2d1e; --cv-border2:#2f331f;
  --cv-text:#F4F7EC; --cv-muted:#8a9070; --cv-muted2:#6b7050;
  --cv-volt:#C3F53B; --cv-volt-h:#a8d932; --cv-volt-dim:rgba(195,245,59,.10);
  --cv-good:#22c55e; --cv-warn:#e5c04b; --cv-bad:#f87171; --cv-info:#c3d2e8;
  --cv-clip:polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px));
  background:var(--cv-bg); color:var(--cv-text); min-height:calc(100vh - 62px);
  font-family:'Saira',system-ui,sans-serif;
}
.cv-shell{max-width:1100px;margin:0 auto;padding:20px 18px 60px}
.cv-h2{font-family:'Khand','Saira',sans-serif;font-size:26px;font-weight:700;margin:0;display:inline}
.cv-rhead{margin-bottom:12px;display:flex;align-items:baseline;gap:10px}
.cv-rsub{font-family:'Chakra Petch',monospace;font-size:10px;color:var(--cv-muted2);text-transform:uppercase;letter-spacing:.08em}
.cv-notice{background:rgba(229,192,75,.1);border:1px solid rgba(229,192,75,.34);border-left:3px solid var(--cv-warn);
  color:var(--cv-warn);padding:10px 14px;margin-bottom:14px;font-size:13px;clip-path:var(--cv-clip)}
.cv-attnbar{display:flex;align-items:center;gap:10px;width:100%;text-align:left;padding:10px 14px;margin-bottom:10px;
  background:var(--cv-card);border:1px solid var(--cv-border);color:var(--cv-muted);cursor:pointer;
  font-family:'Khand','Saira',sans-serif;font-size:15px;font-weight:600;clip-path:var(--cv-clip)}
.cv-attnbar-warn{background:rgba(229,192,75,.08);border-color:rgba(229,192,75,.3);color:var(--cv-warn)}
.cv-attnbar-on{border-color:var(--cv-volt)}
.cv-attnst{margin-left:auto;font-family:'Chakra Petch',monospace;font-size:8px;letter-spacing:.05em;text-transform:uppercase;color:var(--cv-muted2)}
.cv-chips{display:flex;gap:6px;margin-bottom:12px}
.cv-chip{background:var(--cv-card);border:1px solid var(--cv-border);color:var(--cv-muted);
  font-family:'Chakra Petch',monospace;font-size:10px;font-weight:600;padding:6px 12px;cursor:pointer;
  text-transform:uppercase;letter-spacing:.04em}
.cv-chip-on{border-color:var(--cv-volt);color:var(--cv-text);background:var(--cv-volt-dim)}
.cv-tablewrap{overflow-x:auto}
.cv-table{min-width:0}
.cv-tr{display:grid;grid-template-columns:minmax(220px,2fr) repeat(5,minmax(84px,1fr));gap:8px;align-items:center}
.cv-th{position:sticky;top:0;background:var(--cv-bg);z-index:2}
.cv-thc{background:none;border:none;color:var(--cv-muted2);cursor:pointer;text-align:left;
  font-family:'Chakra Petch',monospace;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;
  padding:8px 4px}
.cv-thc-on{color:var(--cv-volt)}
.cv-row{background:var(--cv-card);border:1px solid var(--cv-border);padding:10px 12px;margin-bottom:8px;cursor:pointer;
  transition:border-color .12s;clip-path:var(--cv-clip);font-size:14px;font-variant-numeric:tabular-nums}
.cv-row:hover{border-color:var(--cv-volt-h)}
.cv-row-attn{border-left:3px solid var(--cv-warn)}
.cv-td{min-width:0}
.cv-td-ath{display:flex;align-items:center;gap:10px}
.cv-av{width:34px;height:34px;flex:none;display:inline-flex;align-items:center;justify-content:center;
  font-family:'Khand','Saira',sans-serif;font-weight:700;font-size:14px;color:var(--cv-bg);clip-path:var(--cv-clip)}
.cv-av-lg{width:48px;height:48px;font-size:19px}
.cv-nm{min-width:0;display:flex;flex-direction:column}
.cv-nmn{font-family:'Khand','Saira',sans-serif;font-size:15px;font-weight:600;display:flex;gap:6px;align-items:center;flex-wrap:wrap}
.cv-rel{font-family:'Chakra Petch',monospace;font-size:8px;color:var(--cv-muted2);text-transform:uppercase;letter-spacing:.05em;margin-top:2px}
.cv-injslot{color:var(--cv-border2);font-size:8px;user-select:none}
.cv-flag{font-family:'Chakra Petch',monospace;font-size:8px;font-weight:700;color:var(--cv-warn);
  background:rgba(229,192,75,.12);padding:2px 6px;letter-spacing:.05em}
.cv-vmuted{color:var(--cv-muted2)} .cv-vgood{color:var(--cv-good)} .cv-vbad{color:var(--cv-bad)}
.cv-over{background:none;border:none;color:var(--cv-warn);font:inherit;cursor:pointer;text-decoration:underline dotted;padding:0}
.cv-reveal{background:var(--cv-card2);border-left:2px solid var(--cv-warn);color:var(--cv-muted);
  font-size:12px;padding:8px 12px;margin:-4px 0 8px}
.cv-nomatch{color:var(--cv-muted2);padding:20px;text-align:center;font-size:13px}
.cv-footnote{font-family:'Chakra Petch',monospace;font-size:9px;color:var(--cv-muted2);margin-top:8px;
  text-transform:uppercase;letter-spacing:.04em}
.cv-trust{font-family:'Chakra Petch',monospace;font-size:10px;color:var(--cv-muted2);text-align:center;
  padding:26px 10px 4px;line-height:1.7;letter-spacing:.03em}
.cv-empty{background:var(--cv-card);border:1px solid var(--cv-border);padding:26px;clip-path:var(--cv-clip);
  color:var(--cv-muted);font-size:14px;line-height:1.6;max-width:560px}
.cv-empty-hd{font-family:'Khand','Saira',sans-serif;font-size:20px;font-weight:700;color:var(--cv-text);margin-bottom:6px}
.cv-empty b{color:var(--cv-text)}
.cv-empty-how{margin-top:14px;padding-top:12px;border-top:1px solid var(--cv-border2);font-size:13px}
.cv-skeleton{color:var(--cv-muted2);padding:20px 0;font-size:13px}
.cv-error{background:var(--cv-card);border:1px solid rgba(248,113,113,.34);border-left:3px solid var(--cv-bad);
  padding:16px;color:var(--cv-muted);font-size:13px;display:flex;align-items:center;gap:14px;clip-path:var(--cv-clip)}
.cv-carderr{display:flex;align-items:center;gap:12px;color:var(--cv-muted);font-size:13px;padding:12px 0}
.cv-retry{background:var(--cv-volt-dim);border:1px solid var(--cv-volt);color:var(--cv-volt);cursor:pointer;
  font-family:'Khand','Saira',sans-serif;font-size:13px;font-weight:600;padding:6px 14px;text-transform:uppercase;
  letter-spacing:.04em;clip-path:var(--cv-clip)}
.cv-back{background:none;border:none;color:var(--cv-muted);cursor:pointer;font-family:'Chakra Petch',monospace;
  font-size:11px;text-transform:uppercase;letter-spacing:.06em;padding:0 0 14px}
.cv-back:hover{color:var(--cv-volt)}
.cv-dh{display:flex;align-items:center;gap:14px;margin-bottom:16px;flex-wrap:wrap}
.cv-dsub{font-family:'Chakra Petch',monospace;font-size:10px;color:var(--cv-muted2);text-transform:uppercase;
  letter-spacing:.1em;margin-top:4px}
.cv-spacer{flex:1}
.cv-adjust{background:var(--cv-volt-dim);border:1px solid var(--cv-volt);color:var(--cv-volt);cursor:pointer;
  font-family:'Khand','Saira',sans-serif;font-size:14px;font-weight:600;padding:10px 16px;
  text-transform:uppercase;letter-spacing:.04em;clip-path:var(--cv-clip)}
.cv-adjust:hover{background:rgba(195,245,59,.18)}
.cv-cols{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:16px}
.cv-card{background:var(--cv-card);border:1px solid var(--cv-border);padding:16px;clip-path:var(--cv-clip)}
.cv-card-wide{margin-bottom:16px}
.cv-h3{font-family:'Chakra Petch',monospace;font-size:11px;text-transform:uppercase;letter-spacing:.1em;
  color:var(--cv-muted);margin:0 0 13px;display:flex;align-items:center;gap:8px;font-weight:700}
.cv-h3r{margin-left:auto;color:var(--cv-muted2);letter-spacing:.04em;font-weight:500}
.cv-cardempty{color:var(--cv-muted2);padding:14px 0;font-size:13px}
.cv-musc{display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid var(--cv-border2)}
.cv-musc:last-of-type{border-bottom:0}
.cv-muscname{flex:1;font-family:'Khand','Saira',sans-serif;font-size:15px;font-weight:500}
.cv-pill{font-family:'Chakra Petch',monospace;font-size:9px;font-weight:700;padding:3px 8px;letter-spacing:.05em}
.cv-pill-hi{background:rgba(248,113,113,.16);color:var(--cv-bad)}
.cv-pill-pr{background:rgba(229,192,75,.16);color:var(--cv-warn)}
.cv-pill-tr{background:rgba(195,210,232,.16);color:var(--cv-info)}
.cv-pill-fr{background:rgba(34,197,94,.16);color:var(--cv-good)}
.cv-pill-nt{background:var(--cv-card2);color:var(--cv-muted2)}
.cv-score{font-family:'Khand','Saira',sans-serif;font-size:17px;font-weight:700;width:34px;text-align:right;
  font-variant-numeric:tabular-nums}
.cv-dago{font-family:'Chakra Petch',monospace;font-size:10px;color:var(--cv-muted2);width:54px;text-align:right}
.cv-wsel{display:flex;gap:8px;overflow-x:auto;padding-bottom:6px;margin-bottom:4px}
.cv-w{flex:none;background:var(--cv-card2);border:1px solid var(--cv-border2);color:var(--cv-text);
  padding:8px 13px;cursor:pointer;min-width:120px;text-align:left;clip-path:var(--cv-clip)}
.cv-w-on{border-color:var(--cv-volt);background:var(--cv-volt-dim)}
.cv-wt{display:block;font-family:'Khand','Saira',sans-serif;font-size:15px;font-weight:600}
.cv-wd{display:block;font-family:'Chakra Petch',monospace;font-size:9px;color:var(--cv-muted2);margin-top:2px;letter-spacing:.03em}
.cv-grp{font-family:'Chakra Petch',monospace;font-size:9px;text-transform:uppercase;letter-spacing:.1em;
  color:var(--cv-volt);font-weight:700;margin:14px 0 4px}
.cv-setscroll{overflow-x:auto}
.cv-sets{width:100%;border-collapse:collapse;min-width:520px}
.cv-sets th{font-family:'Chakra Petch',monospace;font-size:8px;color:var(--cv-muted2);text-transform:uppercase;
  letter-spacing:.05em;text-align:right;padding:6px;font-weight:700}
.cv-sets th.cv-l,.cv-sets td.cv-l{text-align:left}
.cv-sets td{font-size:13.5px;padding:9px 6px;border-top:1px solid var(--cv-border2);text-align:right;
  font-variant-numeric:tabular-nums}
.cv-ex{font-family:'Khand','Saira',sans-serif;font-weight:500;font-size:14.5px}
.cv-ss{font-family:'Chakra Petch',monospace;font-size:9px;color:var(--cv-info);font-weight:700}
.cv-pr{font-family:'Chakra Petch',monospace;font-size:9px;color:var(--cv-volt);font-weight:700}
.cv-plan{color:var(--cv-muted)}
.cv-repmiss{color:var(--cv-warn);font-weight:600}
.cv-rephit{color:var(--cv-good)}
.cv-band{font-family:'Chakra Petch',monospace;font-size:11px;color:var(--cv-info);text-transform:uppercase;letter-spacing:.04em}
.cv-notecell{font-size:11.5px;color:var(--cv-muted);font-style:italic;text-align:left !important}
.cv-cap{font-size:11.5px;color:var(--cv-muted);line-height:1.55;margin-top:12px;background:var(--cv-card2);
  padding:10px 12px;border-left:2px solid var(--cv-border2)}
.cv-nogoal{font-size:12px;color:var(--cv-warn);background:rgba(229,192,75,.1);padding:9px 11px;margin-bottom:12px;
  border-left:2px solid var(--cv-warn)}
.cv-bars{display:flex;flex-direction:column;gap:10px}
.cv-bar{display:flex;align-items:center;gap:10px}
.cv-bk{width:60px;font-family:'Chakra Petch',monospace;font-size:10px;color:var(--cv-muted);
  text-transform:uppercase;letter-spacing:.05em}
.cv-track{flex:1;height:8px;background:var(--cv-card2);overflow:hidden;display:block}
.cv-fill{height:100%;background:var(--cv-volt);display:block}
.cv-fill-muted{background:var(--cv-muted2)}
.cv-bv{min-width:110px;text-align:right;font-family:'Khand','Saira',sans-serif;font-size:14px;
  font-variant-numeric:tabular-nums;color:var(--cv-muted)}
.cv-soon{opacity:.5;filter:grayscale(.85);pointer-events:none}
@media (max-width:720px){
  .cv-cols{grid-template-columns:1fr}
  .cv-th{display:none}
  .cv-tr{grid-template-columns:1fr 1fr;gap:4px 10px}
  .cv-td-ath{grid-column:1 / -1;margin-bottom:6px}
  .cv-td[data-label]::before{content:attr(data-label);display:block;font-family:'Chakra Petch',monospace;
    font-size:7px;color:var(--cv-muted2);text-transform:uppercase;letter-spacing:.04em}
  .cv-td[data-label=""]::before{display:none}
}
`;
